import React, { useMemo, useState, useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Container } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectAuth } from 'services/auth/redux/selectors'
import { getAuthPermissions, PERM_UPDATE_CONTENT } from 'services/auth'
import { getIdFromLocation } from 'services/content'
import { selectLocation } from 'services/location/redux/selectors'
import { LANGUAGE_EN } from 'services/languages/constants'
import ContentDetailQueryStates from './ContentDetailQueryStates'
import CourseModuleContentInformationSection from './CourseModuleContentInformationSection'
import CourseModuleContentSection from './course-module-content-section'
import { GET_COURSE_MODULE_CONTENT, UPDATE_COURSE_CONTENT } from './queries'
import FloatingSaveCancelButtons from './FloatingSaveCancelButtons'
import ContentDetailSnackbar, { useContentDetailSnackbar } from './ContentDetailSnackbar'
import type { ContentInfoChanges } from './types'
import './course-detail.scss'

/**
 * Course Module Detail Component
 * Displays and allows editing of Course Module content
 *
 * This component uses Apollo Client for data fetching (modern YI pattern)
 * No Redux for content data - only for auth and location
 */
function CourseModuleDetail () {
  const auth = useSelector(selectAuth)
  const location = useSelector(selectLocation)
  const authPermissions = getAuthPermissions(auth)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)

  // Get content ID from URL
  const id = getIdFromLocation(location)

  // Fetch course module data from GraphQL
  const { loading, error, data } = useQuery(GET_COURSE_MODULE_CONTENT, {
    variables: { id },
    skip: !id,
  })

  // Get data from GraphQL response as plain object
  const moduleData = useMemo(() => data?.contentList?.content?.[0] || null, [data])

  // Extract language for content references (passed to child component)
  const moduleLanguage = useMemo(() => moduleData?.language || LANGUAGE_EN, [moduleData])

  // Track dirty state from Content Information section
  const [contentInfoDirty, setContentInfoDirty] = useState<boolean>(false)

  // Store changed values from Content Information section
  const [contentInfoChanges, setContentInfoChanges] = useState<ContentInfoChanges | null>(null)

  // Track dirty state from Module Content section
  const [moduleContentDirty, setModuleContentDirty] = useState<boolean>(false)

  // Store the save handler from Module Content section
  const [moduleContentSaveHandler, setModuleContentSaveHandler] = useState<
    (() => Promise<void>) | null
  >(null)

  // Track reset trigger counter
  const [resetTrigger, setResetTrigger] = useState<number>(0)

  // Overall dirty state (includes both content info and course module content)
  const isDirty = contentInfoDirty || moduleContentDirty

  // Handle dirty change from Content Information
  const handleContentInfoDirtyChange = useCallback((isContentDirty: boolean) => {
    setContentInfoDirty(isContentDirty)
  }, [])

  // Handle changes update from Content Information
  const handleContentInfoChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setContentInfoChanges(changes)
  }, [])

  // Handle dirty change from Module Content section
  const handleModuleContentDirtyChange = useCallback((isModuleContentDirty: boolean) => {
    setModuleContentDirty(isModuleContentDirty)
  }, [])

  // Handle save from Module Content section
  const handleModuleContentSave = useCallback((saveHandler: () => Promise<void>) => {
    setModuleContentSaveHandler(() => saveHandler)
  }, [])

  // Mutation for updating course module content
  const [updateModuleContent, { loading: saving }] = useMutation(UPDATE_COURSE_CONTENT, {
    refetchQueries: [{ query: GET_COURSE_MODULE_CONTENT, variables: { id } }],
    awaitRefetchQueries: true,
  })

  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useContentDetailSnackbar()

  // Handle save
  const handleSave = async () => {
    try {
      const hasContentChanges = !!contentInfoChanges
      const hasModuleContentChanges = moduleContentDirty && moduleContentSaveHandler

      if (!hasContentChanges && !hasModuleContentChanges) {
        showSnackbar('No changes to save', 'info')
        return
      }

      if (hasContentChanges) {
        await updateModuleContent({
          variables: {
            content: contentInfoChanges.contentInput,
            generatePath: contentInfoChanges.generatePath,
          },
        })
      }

      if (hasModuleContentChanges && moduleContentSaveHandler) {
        await moduleContentSaveHandler()
      }

      showSnackbar('Course Module content saved successfully!', 'success')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      showSnackbar(`Error saving content: ${errorMessage}`, 'error')
    }
  }

  const handleCancel = () => {
    setResetTrigger((prev) => prev + 1)
    showSnackbar('Changes discarded', 'info')
  }

  return (
    <ContentDetailQueryStates
      loading={loading}
      error={error}
      hasData={!!moduleData}
      errorTitle="Error loading Course Module"
      notFoundTitle="Course Module not found"
      notFoundDetail={`No Course Module found with ID: ${id}`}
    >
      <Container className="course-module-detail content-detail" maxWidth={false}>
        {/* Content Information Section */}
        <Container className="content-detail__section content-information" maxWidth={false}>
          <CourseModuleContentInformationSection
            contentData={moduleData}
            isViewOnly={isViewOnlyMode}
            onDirtyChange={handleContentInfoDirtyChange}
            onChangesUpdate={handleContentInfoChangesUpdate}
            onResetTriggered={resetTrigger}
          />
        </Container>

        {/* Course Module Content Section */}
        <Container className="content-detail__section course-module-content" maxWidth={false}>
          <CourseModuleContentSection
            moduleId={id!}
            moduleContentId={moduleData?.contentId}
            moduleLanguage={moduleLanguage}
            disabled={isViewOnlyMode}
            onDirtyChange={handleModuleContentDirtyChange}
            onSaveHandler={handleModuleContentSave}
            onResetTriggered={resetTrigger}
          />
        </Container>

        {!isViewOnlyMode && (
          <FloatingSaveCancelButtons
            visible={isDirty}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )}

        <ContentDetailSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
        />
      </Container>
    </ContentDetailQueryStates>
  )
}

export default CourseModuleDetail
